import { ILogger } from '@principle-theorem/developer-tools';
import {
  DestinationEntityRecordStatus,
  IDestinationEntityHandler,
  IDestinationEntityRecord,
  IDestinationJobFilter,
  isDestinationEntityRecord,
  SourceEntityRecordMigrationStatus,
  type IDestinationEntity,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Firestore,
  getError,
  resolveWithConcurrencyForAll,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { DestinationEntity } from './destination/destination-entity';
import { DestinationEntityRecord } from './destination/destination-entity-record';
import { DestinationHandler } from './destination/destination-handler';
import { SourceEntityRecord } from './source/source-entity-record';
import { TranslationMapHandler } from './translation-map';

export interface IMigrationJobData {
  migration: WithRef<IPracticeMigration>;
  entity: WithRef<IDestinationEntity>;
  filters: IDestinationJobFilter[];
  skipMergeConflictCheck?: boolean;
  dryRun?: boolean;
  data: object[];
}

export class MigrateJobRunner {
  constructor(private readonly _logger: ILogger = console) {}

  async run(data: IMigrationJobData): Promise<void> {
    const destinationHandler = DestinationHandler.getDestinationHandler(
      data.migration
    );
    if (!destinationHandler) {
      throw new Error(
        `MigrateJobRunner.run - Couldn't find destination handler`
      );
    }

    const destinationEntityHandler =
      DestinationHandler.getDestinationEntityHandler(
        data.migration,
        data.entity
      );

    const destinationEntity = await Firestore.getDoc(data.entity.ref);

    if (!destinationEntityHandler) {
      throw new Error(
        `MigrateJobRunner.run - Couldn't find destination entity handler`
      );
    }

    const translationMap = new TranslationMapHandler(data.migration);

    try {
      await resolveWithConcurrencyForAll(
        data.data,
        destinationEntityHandler.concurrency,
        async (record) => {
          const recordUid =
            destinationEntityHandler.getDestinationEntityRecordUid(record);

          try {
            const shouldSkipRecord = destinationEntityHandler.shouldSkipRecord(
              record,
              data.filters
            );

            if (shouldSkipRecord) {
              this._logger.info(
                `MigrateJobRunner.run - Skipping record ${recordUid}`
              );
              return;
            }

            const migrationData =
              await destinationEntityHandler.buildMigrationData(
                data.migration,
                data.entity,
                translationMap,
                record
              );

            const shouldSkipDataMigration =
              isDestinationEntityRecord(migrationData);
            if (shouldSkipDataMigration) {
              await this._updateDestinationEntityRecord(
                data,
                destinationEntityHandler,
                record,
                recordUid,
                migrationData
              );

              return;
            }

            if (data.skipMergeConflictCheck !== true) {
              const mergeConflictData =
                await destinationEntityHandler.hasMergeConflict(
                  translationMap,
                  migrationData
                );

              if (mergeConflictData) {
                await this._saveMergeConflictRecord(
                  data,
                  destinationEntityHandler,
                  translationMap,
                  record,
                  recordUid,
                  migrationData,
                  mergeConflictData
                );

                return;
              }
            }

            await this._runMigrationJob(
              recordUid,
              destinationEntityHandler,
              data,
              translationMap,
              record,
              migrationData
            );
          } catch (error) {
            const jobKey = data.entity.metadata.key;

            await SourceEntityRecord.setMigratedByDestination(
              data.migration,
              jobKey,
              destinationEntityHandler.sourceCountDataAccessor(record),
              SourceEntityRecordMigrationStatus.Failed
            );

            this._logger.error(
              `MigrateJobRunner.run - ${jobKey} record ${recordUid} failed - ${getError(
                error
              )}`,
              error
            );
          }
        }
      );
    } catch (error) {
      this._logger.error(
        `Error processing destination records - ${getError(error)}`,
        error
      );
    } finally {
      await DestinationEntity.setLastRunAt(destinationEntity);
    }
  }

  private async _updateDestinationEntityRecord(
    data: IMigrationJobData,
    destinationEntityHandler: IDestinationEntityHandler,
    record: object,
    recordUid: string,
    migrationData: IDestinationEntityRecord
  ): Promise<void> {
    const destinationRecord = migrationData;
    const migrationStatus = destinationRecord.status;

    this._logger.error(
      `MigrateJobRunner.run - Failed record ${recordUid} - ${
        migrationStatus === DestinationEntityRecordStatus.Failed
          ? destinationRecord.errorMessage
          : ''
      }`
    );

    await DestinationEntityRecord.upsert(
      data.migration,
      [
        {
          uid: recordUid,
          label: destinationRecord.label,
          status: migrationStatus,
          sourceRef: destinationRecord.sourceRef,
          migratedAt: toTimestamp(),
          errorMessage:
            migrationStatus === DestinationEntityRecordStatus.Failed ||
            migrationStatus ===
              DestinationEntityRecordStatus.MissingCustomMapping
              ? destinationRecord.errorMessage
              : undefined,
          failData:
            migrationStatus === DestinationEntityRecordStatus.Failed ||
            migrationStatus ===
              DestinationEntityRecordStatus.MissingCustomMapping
              ? destinationRecord.failData
              : {},
        } as IDestinationEntityRecord,
      ],
      destinationEntityHandler.destinationEntity.metadata.key
    );

    if (migrationStatus === DestinationEntityRecordStatus.Failed) {
      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Failed
      );
      this._logger.error(
        `MigrateJobRunner.run - Migration for record ${recordUid} failed with message: ${destinationRecord.errorMessage}`
      );
      return;
    }

    if (
      [
        DestinationEntityRecordStatus.Skipped,
        DestinationEntityRecordStatus.MergeConflict,
      ].includes(migrationData.status)
    ) {
      this._logger.info(
        `MigrateJobRunner.run - Skipping record ${data.entity.ref.path}/records/${recordUid} - ${migrationData.status}`
      );
      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Migrated
      );
    }
  }

  private async _saveMergeConflictRecord(
    data: IMigrationJobData,
    destinationEntityHandler: IDestinationEntityHandler,
    translationMap: TranslationMapHandler,
    record: object,
    recordUid: string,
    migrationData: object,
    mergeConflictData: object
  ): Promise<void> {
    const destinationRecord = destinationEntityHandler.buildMergeConflictRecord(
      data.migration,
      data.entity,
      translationMap,
      record,
      migrationData
    );

    this._logger.info(
      `MigrateJobRunner.run - Destination record has merge conflict ${data.entity.ref.path}/records/${destinationRecord.uid}`
    );

    try {
      await DestinationEntityRecord.upsert(
        data.migration,
        [
          {
            uid: recordUid,
            label: destinationRecord.label,
            status: destinationRecord.status,
            data: migrationData,
            diffData: destinationRecord.diffData,
            sourceRef: destinationRecord.sourceRef,
          },
        ],
        destinationEntityHandler.destinationEntity.metadata.key
      );
    } catch (subError) {
      this._logger.error(
        `MigrateJobRunner.run - data record ${recordUid} failed to update - ${getError(
          subError
        )}`,
        subError
      );
      return;
    }

    try {
      await DestinationEntityRecord.upsert(
        data.migration,
        [
          {
            uid: recordUid,
            label: destinationRecord.label,
            status: destinationRecord.status,
            data: migrationData,
            jobData: record,
            diffData: mergeConflictData,
            sourceRef: destinationRecord.sourceRef,
          },
        ],
        destinationEntityHandler.destinationEntity.metadata.key
      );

      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Migrated
      );
    } catch (error) {
      this._logger.error(
        `MigrateJobRunner.run - diff data record ${recordUid} failed - ${String(
          error
        )}`,
        error
      );
    }
  }

  private async _runMigrationJob(
    recordUid: string,
    destinationEntityHandler: IDestinationEntityHandler,
    data: IMigrationJobData,
    translationMap: TranslationMapHandler,
    record: object,
    migrationData: object
  ): Promise<void> {
    if (data.dryRun) {
      this._logger.info(
        `MigrateJobRunner.run - Dry run for record ${recordUid} - skipping migration job`
      );

      const skippedRecord =
        destinationEntityHandler.buildSkippedResponse(record);
      await DestinationEntityRecord.upsert(
        data.migration,
        [
          {
            uid: recordUid,
            label: skippedRecord.label,
            status: skippedRecord.status,
            data: skippedRecord.data,
            sourceRef: skippedRecord.sourceRef,
            migratedAt: toTimestamp(),
            migrationData,
            failData: {},
          } as IDestinationEntityRecord,
        ],
        destinationEntityHandler.destinationEntity.metadata.key
      );

      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Migrated
      );
      return;
    }

    this._logger.info(
      `MigrateJobRunner.run - Running job for record ${recordUid}`
    );
    const destinationRecord = await destinationEntityHandler.runJob(
      data.migration,
      data.entity,
      translationMap,
      record,
      migrationData
    );

    await DestinationEntityRecord.upsert(
      data.migration,
      [
        {
          uid: recordUid,
          label: destinationRecord.label,
          status: destinationRecord.status,
          data: destinationRecord.data,
          sourceRef: destinationRecord.sourceRef,
          migratedAt: toTimestamp(),
          migrationData,
          failData:
            destinationRecord.status ===
              DestinationEntityRecordStatus.MigratedWithErrors ||
            destinationRecord.status === DestinationEntityRecordStatus.Failed ||
            destinationRecord.status ===
              DestinationEntityRecordStatus.MissingCustomMapping
              ? destinationRecord.failData
              : {},
        } as IDestinationEntityRecord,
      ],
      destinationEntityHandler.destinationEntity.metadata.key
    );

    if (
      [
        DestinationEntityRecordStatus.Skipped,
        DestinationEntityRecordStatus.MergeConflict,
        DestinationEntityRecordStatus.Migrated,
        DestinationEntityRecordStatus.MigratedWithErrors,
      ].includes(destinationRecord.status)
    ) {
      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Migrated
      );
    }

    if (destinationRecord.status === DestinationEntityRecordStatus.Failed) {
      await SourceEntityRecord.setMigratedByDestination(
        data.migration,
        data.entity.metadata.key,
        destinationEntityHandler.sourceCountDataAccessor(record),
        SourceEntityRecordMigrationStatus.Failed
      );
      this._logger.error(
        `MigrateJobRunner.run - Migration for record ${recordUid} failed with message: ${destinationRecord.errorMessage}`
      );
    }
  }
}
